import React, { useEffect, useState, useCallback } from 'react';
import { FiAward, FiRefreshCw, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import Modal from '../Modal/Modal';
import { useApi } from '../../hooks/useApi';

/**
 * WinningMarketsModal
 * Renders the winning markets list returned by the backend for a fictive match
 * (or per-integration fictive match).
 *
 * Props:
 *  - open, onClose
 *  - title
 *  - fictiveMatchId   (required for fictive scope)
 *  - integrationId    (optional; when provided, integration-scope endpoint is used)
 *  - canEvaluate      (default true) — show "Re-evaluate" admin button
 */
export default function WinningMarketsModal({
  open,
  onClose,
  title,
  fictiveMatchId,
  integrationId,
  canEvaluate = true,
}) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [error, setError] = useState(null);

  const basePath = integrationId
    ? `/v1/clasico-integrations/${integrationId}/fictive-matches/${fictiveMatchId}/winning-markets`
    : `/v1/clasico-winning-markets/${fictiveMatchId}`;

  const load = useCallback(async () => {
    if (!fictiveMatchId) return;
    setLoading(true); setError(null);
    try {
      const res = await api.get(basePath);
      setData(res);
    } catch (e) {
      // 404 means "not yet evaluated"; keep data as null and show a friendly note
      if (/404/.test(e?.message)) {
        setData(null);
        setError(null);
      } else {
        setError(e?.message || 'Failed to load winning markets');
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath, fictiveMatchId]);

  const evaluate = async () => {
    setEvaluating(true); setError(null);
    try {
      const res = await api.post(`${basePath}/evaluate`, {});
      setData(res);
    } catch (e) {
      setError(e?.message || 'Failed to evaluate');
    } finally {
      setEvaluating(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const status = data?.status;
  const statusColor = {
    EVALUATED: '#73c991',
    PENDING:   '#d4a052',
    READY:     '#a8c1e8',
    ERROR:     '#e06c75',
  }[status] || 'var(--text-muted)';

  return (
    <Modal open={open} onClose={onClose} title={title || 'Winning Markets'} xl>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
          background: 'rgba(255,255,255,0.04)', border: `1px solid ${statusColor}`, color: statusColor,
        }}>
          <FiAward size={12} /> {status || 'NOT EVALUATED'}
        </span>
        {data?.evaluatedAt && (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Evaluated: {new Date(data.evaluatedAt).toLocaleString()}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            onClick={load}
            disabled={loading || evaluating}
            style={btnStyle('outline')}
          >
            <FiRefreshCw size={12} /> Refresh
          </button>
          {canEvaluate && (
            <button
              onClick={evaluate}
              disabled={loading || evaluating}
              style={btnStyle('primary')}
            >
              {evaluating ? 'Evaluating…' : 'Re-evaluate'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={alertStyle('#e06c75')}>
          <FiAlertTriangle /> {error}
        </div>
      )}

      {status === 'ERROR' && data?.errorMessage && (
        <div style={alertStyle('#e06c75')}>
          <FiAlertTriangle /> Evaluator error: {data.errorMessage}
        </div>
      )}

      {status === 'PENDING' && (
        <div style={alertStyle('#d4a052')}>
          <FiInfo /> One or both source real matches are not yet finished.
          Partial winning markets may already be available below.
        </div>
      )}

      {loading && <div style={{ color: 'var(--text-muted)', padding: 20, textAlign: 'center' }}>Loading…</div>}

      {!loading && !data && !error && (
        <div style={{ color: 'var(--text-muted)', padding: 20, textAlign: 'center' }}>
          No winning markets row exists yet for this match.
        </div>
      )}

      {!loading && data && Array.isArray(data.winningMarkets) && (
        <WinningMarketsTable items={data.winningMarkets} />
      )}
    </Modal>
  );
}

function WinningMarketsTable({ items }) {
  if (!items.length) {
    return <div style={{ color: 'var(--text-muted)', padding: 16, textAlign: 'center' }}>No winning markets.</div>;
  }
  // Group by `group` field for readability
  const grouped = items.reduce((acc, m) => {
    const k = m.group || 'OTHER';
    (acc[k] = acc[k] || []).push(m);
    return acc;
  }, {});
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {Object.entries(grouped).map(([group, list]) => (
        <div key={group}>
          <div style={{
            fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px',
            color: 'var(--text-muted)', marginBottom: 6,
          }}>{group} <span style={{ color: 'var(--text-muted)', textTransform: 'none', fontWeight: 400 }}>({list.length})</span></div>
          <div style={{
            border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: 'var(--bg-primary)', textAlign: 'left' }}>
                  <th style={th}>Code</th>
                  <th style={th}>Name</th>
                  <th style={th}>Period</th>
                  <th style={th}>Stat</th>
                  <th style={th}>Outcome</th>
                  <th style={th}>Side</th>
                  <th style={{ ...th, textAlign: 'right' }}>Line</th>
                  <th style={{ ...th, textAlign: 'right' }}>Odds</th>
                </tr>
              </thead>
              <tbody>
                {list.map((m, i) => (
                  <tr key={`${m.code}-${i}`} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace', color: 'var(--text-secondary)' }}>{m.code}</td>
                    <td style={td}>{m.name}</td>
                    <td style={td}>{m.period || '—'}</td>
                    <td style={td}>{m.statKind || '—'}</td>
                    <td style={td}>{m.outcome || '—'}</td>
                    <td style={td}>{m.side || '—'}{m.teamSide ? ` (${m.teamSide})` : ''}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{m.line ?? (m.handicap ?? '—')}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#73c991' }}>
                      {m.decimalOdds != null ? Number(m.decimalOdds).toFixed(2) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

const th = { padding: '8px 10px', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' };
const td = { padding: '7px 10px', color: 'var(--text-primary)' };

const btnStyle = (kind) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600,
  borderRadius: 6, cursor: 'pointer',
  background: kind === 'primary' ? 'var(--accent)' : 'transparent',
  color: kind === 'primary' ? '#fff' : 'var(--text-primary)',
  border: kind === 'primary' ? '1px solid var(--accent)' : '1px solid var(--border-color)',
});

const alertStyle = (color) => ({
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 12px', borderRadius: 8, marginBottom: 12,
  background: 'rgba(255,255,255,0.03)', border: `1px solid ${color}`,
  color, fontSize: '0.82rem',
});
